import React, { useState } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemText,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import {
  QrCode2 as QrCodeIcon,
  Launch as LaunchIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { surveyApi } from '../../survey/api/surveyApi';
import { useContextStore } from '../../../stores/contextStore';
import { useAuthStore } from '../../authentication/store/authStore';
import { useMySubjects } from '../../survey/hooks/useLecturerSubjects';
import { SurveyDTO } from '../../survey/dtos';

interface ShareQRCodeDropdownProps {
  size?: 'small' | 'medium' | 'large';
  variant?: 'text' | 'outlined' | 'contained';
}

export const ShareQRCodeDropdown: React.FC<ShareQRCodeDropdownProps> = ({
  size = 'large',
  variant = 'contained',
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { user } = useAuthStore();
  const { activeTenantId, activeSubjectId, activeDomainId } = useContextStore();

  // Get subjects to find the active subject
  const { data: subjectsData } = useMySubjects(
    user?.id || '',
    activeTenantId || '',
    activeDomainId || undefined
  );
  const subjects = subjectsData?.items;
  const activeSubject =
    subjects?.find((subject) => subject.subjectId === activeSubjectId) || subjects?.[0];

  // Fetch surveys for the active subject
  const {
    data: surveysResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['surveys', activeSubject?.subjectId],
    queryFn: () => surveyApi.getSurveysBySubject(activeSubject!.subjectId),
    enabled: !!activeSubject?.subjectId,
  });

  const surveys = surveysResponse?.data || [];
  const activeSurveys = surveys.filter((survey: SurveyDTO) => survey.status === 'ACTIVE');

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSurveySelect = (surveyId: string) => {
    handleClose();
    // Navigate to the survey instance page (same action as "Go to survey" button)
    navigate(`/app/surveys/${surveyId}/instance`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'DRAFT':
        return 'warning';
      case 'CLOSED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'Active';
      case 'DRAFT':
        return 'Draft';
      case 'CLOSED':
        return 'Closed';
      default:
        return status;
    }
  };

  // If no active subject, show disabled button
  if (!activeSubject) {
    return (
      <Button variant={variant} size={size} startIcon={<QrCodeIcon />} disabled>
        Share QR Link
      </Button>
    );
  }

  return (
    <>
      <Button
        variant={variant}
        size={size}
        startIcon={<QrCodeIcon />}
        endIcon={<ArrowDownIcon />}
        onClick={handleClick}
        disabled={isLoading}
      >
        Share QR Link
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 300,
            maxWidth: 400,
            maxHeight: 400,
            '& .MuiMenuItem-root': {
              py: 1.5,
              px: 2,
            },
          },
        }}
        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
            Select Survey to Share
          </Typography>
          {activeSubject && (
            <Typography variant="caption" color="text.secondary">
              {activeSubject.name} ({activeSubject.code})
            </Typography>
          )}
        </Box>

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {error && (
          <Box sx={{ px: 2, py: 2 }}>
            <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>
              Failed to load surveys
            </Alert>
          </Box>
        )}

        {!isLoading && !error && surveys.length === 0 && (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              No surveys found for this subject
            </Typography>
          </Box>
        )}

        {!isLoading && !error && activeSurveys.length === 0 && surveys.length > 0 && (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              No active surveys available. Activate a survey first.
            </Typography>
          </Box>
        )}

        {!isLoading && !error && activeSurveys.length > 0 && (
          <>
            {/* Active surveys section */}
            <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
              <Typography variant="caption" fontWeight={500} color="text.secondary">
                ACTIVE SURVEYS ({activeSurveys.length})
              </Typography>
            </Box>
            {activeSurveys.map((survey: SurveyDTO) => (
              <MenuItem
                key={survey.id}
                onClick={() => handleSurveySelect(survey.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <LaunchIcon fontSize="small" color="primary" />
                <ListItemText
                  primary={survey.title}
                  secondary={survey.description || 'No description'}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: 500,
                  }}
                  secondaryTypographyProps={{
                    variant: 'caption',
                    color: 'text.secondary',
                    sx: {
                      lineHeight: 1.2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                    },
                  }}
                />
                <Chip
                  label={getStatusLabel(survey.status)}
                  color={getStatusColor(survey.status)}
                  variant="outlined"
                  size="small"
                />
              </MenuItem>
            ))}
          </>
        )}
      </Menu>
    </>
  );
};
